/**
 * RTK Query endpoints for platform organization detail page.
 * Covers: org detail, users, integrations, and org-level mutations.
 *
 * Phase 1 of Q9b: endpoint definitions only. Hooks not yet consumed by page.
 */

import { baseApi } from '@/api/core/baseApi';
import { unwrapApiResponse, unwrapListResponse } from '@/api/patterns/crud';
import type {
  OrgDetailOrganization,
  OrgDetailUser,
  OrgDetailIntegration,
  DeleteOrgUserArgs,
  CreateIntegrationArgs,
  UpdateIntegrationArgs,
  DeleteIntegrationArgs,
  ToggleOrgGuardrailsArgs,
  DeleteOrgArgs,
} from '../types';

export const organizationDetailApi = baseApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    // ── Queries ─────────────────────────────────────────────────

    getOrganizationDetail: builder.query<OrgDetailOrganization, string>({
      query: (orgId) => `/platform/organizations/${orgId}`,
      transformResponse: unwrapApiResponse<OrgDetailOrganization>,
      providesTags: (_result, _error, orgId) => [
        { type: 'PlatformOrgDetail', id: orgId },
      ],
    }),

    getOrganizationUsers: builder.query<OrgDetailUser[], string>({
      query: (orgId) => `/platform/organizations/${orgId}/users`,
      transformResponse: unwrapListResponse<OrgDetailUser>,
      providesTags: (_result, _error, orgId) => [
        { type: 'PlatformOrgUsers', id: orgId },
      ],
    }),

    getOrganizationIntegrations: builder.query<OrgDetailIntegration[], string>({
      query: (orgId) => `/platform/organizations/${orgId}/integrations`,
      transformResponse: unwrapListResponse<OrgDetailIntegration>,
      providesTags: (_result, _error, orgId) => [
        { type: 'PlatformOrgIntegrations', id: orgId },
      ],
    }),

    // ── Mutations ───────────────────────────────────────────────

    deleteOrgUser: builder.mutation<unknown, DeleteOrgUserArgs>({
      query: ({ userId, confirmation, stepUpToken, stepUpTimestamp }) => ({
        url: `/platform/users/${userId}`,
        method: 'DELETE',
        body: { confirmation },
        headers: stepUpToken ? { 'X-Step-Up-Token': stepUpToken, ...(stepUpTimestamp && { 'X-Step-Up-Timestamp': stepUpTimestamp }) } : undefined,
      }),
      invalidatesTags: (_result, _error, { orgId }) => [
        { type: 'PlatformOrgUsers', id: orgId },
      ],
    }),

    createOrgIntegration: builder.mutation<OrgDetailIntegration, CreateIntegrationArgs>({
      query: ({ orgId, payload }) => ({
        url: `/platform/organizations/${orgId}/integrations`,
        method: 'POST',
        body: payload,
      }),
      transformResponse: unwrapApiResponse<OrgDetailIntegration>,
      invalidatesTags: (_result, _error, { orgId }) => [
        { type: 'PlatformOrgIntegrations', id: orgId },
        { type: 'PlatformOrgProjectedBilling', id: orgId },
      ],
    }),

    updateOrgIntegration: builder.mutation<OrgDetailIntegration, UpdateIntegrationArgs>({
      query: ({ orgId, integrationId, payload }) => ({
        url: `/platform/organizations/${orgId}/integrations/${integrationId}`,
        method: 'PUT',
        body: payload,
      }),
      transformResponse: unwrapApiResponse<OrgDetailIntegration>,
      invalidatesTags: (_result, _error, { orgId }) => [
        { type: 'PlatformOrgIntegrations', id: orgId },
        { type: 'PlatformOrgProjectedBilling', id: orgId },
      ],
    }),

    deleteOrgIntegration: builder.mutation<unknown, DeleteIntegrationArgs>({
      query: ({ orgId, integrationId, stepUpToken, stepUpTimestamp }) => ({
        url: `/platform/organizations/${orgId}/integrations/${integrationId}`,
        method: 'DELETE',
        headers: stepUpToken ? { 'X-Step-Up-Token': stepUpToken, ...(stepUpTimestamp && { 'X-Step-Up-Timestamp': stepUpTimestamp }) } : undefined,
      }),
      invalidatesTags: (_result, _error, { orgId }) => [
        { type: 'PlatformOrgIntegrations', id: orgId },
        { type: 'PlatformOrgProjectedBilling', id: orgId },
      ],
    }),

    toggleOrgGuardrails: builder.mutation<unknown, ToggleOrgGuardrailsArgs>({
      query: ({ orgId, guardrailsEnabled }) => ({
        url: `/platform/organizations/${orgId}/guardrails`,
        method: 'POST',
        body: { guardrailsEnabled },
      }),
      invalidatesTags: (_result, _error, { orgId }) => [
        { type: 'PlatformOrgDetail', id: orgId },
      ],
    }),

    deleteOrganizationDetail: builder.mutation<unknown, DeleteOrgArgs>({
      query: ({ id, confirmation, stepUpToken, stepUpTimestamp }) => ({
        url: `/platform/organizations/${id}`,
        method: 'DELETE',
        body: { confirmation },
        headers: stepUpToken ? { 'X-Step-Up-Token': stepUpToken, ...(stepUpTimestamp && { 'X-Step-Up-Timestamp': stepUpTimestamp }) } : undefined,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'PlatformOrganizations', id: 'LIST' },
        { type: 'PlatformOrgDetail', id },
        { type: 'PlatformOrgUsers', id },
        { type: 'PlatformOrgIntegrations', id },
      ],
    }),
  }),
});

export const {
  useGetOrganizationDetailQuery,
  useGetOrganizationUsersQuery,
  useGetOrganizationIntegrationsQuery,
  useDeleteOrgUserMutation,
  useCreateOrgIntegrationMutation,
  useUpdateOrgIntegrationMutation,
  useDeleteOrgIntegrationMutation,
  useToggleOrgGuardrailsMutation,
  useDeleteOrganizationDetailMutation,
} = organizationDetailApi;
